// hooks/useChatRooms.ts
import { useMemo } from 'react';

import { useChatRoomsQuery } from '../api/queries';
import { useChatViewerRole } from './useChatViewerRole';
import { useGlobalPresence } from '../store/GlobalPresenceContext';
import type { ChatRoom } from '../types';
import type { ChatRoomStatus } from '../api/routes';
import { roomMatchesViewerRole } from '../utils/filterRoomsByRole';
import { isAccessibleChatRoom } from '../utils/isAccessibleChat';
import { mapApiRoomToChatRoom } from '../utils/mapApiRoomToChatRoom';

export const useChatRooms = (
  currentUserId: number,
  status: ChatRoomStatus = 'active',
  enabled = true,
) => {
  const viewerRole = useChatViewerRole();
  const { data: apiRooms, isLoading, error, refetch } = useChatRoomsQuery(
    viewerRole,
    status,
    enabled,
  );
  const { isUserOnline } = useGlobalPresence();

  const chatRooms = useMemo(() => {
    if (!apiRooms) return [];

    const seenRoomIds = new Set<string>();
    const seenResponseIds = new Set<number>();
    const uniqueApiRooms = apiRooms.filter((room) => {
      if (room.room_id && seenRoomIds.has(room.room_id)) {
        return false;
      }
      const responseId = room.related_bulletins?.[0]?.bulletin_response_id;
      if (responseId != null && seenResponseIds.has(responseId)) {
        return false;
      }
      if (room.room_id) {
        seenRoomIds.add(room.room_id);
      }
      if (responseId != null) {
        seenResponseIds.add(responseId);
      }
      return true;
    });

    const rooms = uniqueApiRooms
      .filter((room) => roomMatchesViewerRole(room, currentUserId, viewerRole))
      .filter((room) =>
        isAccessibleChatRoom({
          room_id: room.room_id,
          other_user_id: room.other_user?.id_user,
        }),
      )
      .map((room) => mapApiRoomToChatRoom(room, isUserOnline))
      .filter((room): room is ChatRoom => room != null)
      .sort((a, b) => {
        const aTime = new Date(a.last_message?.created_at || a.date_start || 0).getTime();
        const bTime = new Date(b.last_message?.created_at || b.date_start || 0).getTime();
        return bTime - aTime;
      });

    return rooms;
  }, [apiRooms, currentUserId, viewerRole, isUserOnline]);

  return {
    chatRooms,
    isLoading,
    error: error ? (error as Error).message : null,
    refetch,
  };
};
